import React from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getCutoffTime, getDayOfWeek } from '../../services/dateUtils';
import { groupDatesByWeek, formatWeekHeader } from '../../utils/weekUtils';
import SkipDelivery from '../SkipDelivery/SkipDelivery';
import { formatDeliveryDate } from '../../services/dateUtils';
const TAB_WIDTH = 160;
dayjs.extend(utc);
dayjs.extend(timezone);

const DeliveryDateTabs = ({
    deliveryDates,
    selectedDeliveryDate,
    ordersByDeliveryDate,
    deliveryStatusState,
    onDeliveryDateChange,
    // Skip delivery props
    customerToken,
    isLocked,
    onSkipConfirmed,
    customerDetails
}) => {
    if (!deliveryDates) return null;

    // Group dates by week
    const datesByWeek = groupDatesByWeek(deliveryDates);
    const subscriptions = customerDetails?.subscriptions || [];

    //console.log('subscriptions', subscriptions);

    // Helper function to format lock date (Date object)
    const formatLockDate = (lockDate) => {
        if (!lockDate) return '';

        return lockDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            timeZone: 'America/New_York'
        }) + ', ' + lockDate.toLocaleTimeString('en-US', {
            hour: 'numeric',
            hour12: true,
            timeZone: 'America/New_York'
        });
    };

    // Calculate weekly meal counts
    const calculateWeekMealCounts = (datesInWeek) => {
        let weeklyOrdered = 0;
        let weeklySubscribed = 0;

        datesInWeek.forEach(date => {
            const orders = ordersByDeliveryDate[date] || [];
            weeklyOrdered += orders.filter(o => o.isOrdered && o.quantity > 0).reduce((sum, o) => sum + o.quantity, 0);

            // Sum subscriptions for this date
            const dateSubs = subscriptions.filter(sub =>
                getDayOfWeek(date).toLowerCase() === sub.deliveryDay?.toLowerCase()
            );
            weeklySubscribed += dateSubs.reduce((sum, s) => sum + (s.mealsIncluded || 0), 0);
        });

        return { weeklyOrdered, weeklySubscribed };
    };

    // Get weekly subscriptions summary
    const getWeeklySubscriptionsSummary = () => {
        // Calculate total meals by type and track premium status
        const mealsByType = subscriptions.reduce((acc, sub) => {
            const mealType = sub.mealType || 'Meal';
            if (!acc[mealType]) {
                acc[mealType] = { count: 0, isPremium: false };
            }
            acc[mealType].count += (sub.mealsIncluded || 0);
            // Mark as premium if any subscription of this type has the premium tag
            if (sub.tags && sub.tags.includes('Premium Meat Only Paid')) {
                acc[mealType].isPremium = true;
            }
            return acc;
        }, {});

        // Get unique delivery days
        const deliveryDays = [...new Set(subscriptions.map(sub => sub.deliveryDay).filter(Boolean))];

        // Format the summary string
        const parts = Object.entries(mealsByType).map(([type, { count, isPremium }]) => {
            const pluralSuffix = count !== 1 ? (type.toLowerCase() === 'lunch' ? 'es' : 's') : '';
            const premiumLabel = isPremium ? ' (Premium)' : '';
            return `${count} ${type}${pluralSuffix}${premiumLabel}`;
        });
        const totalSubscriptions = "Your Plan";
        const mealSummary = parts.length > 0 ? parts.join(', ') : 'No Active Subscriptions';
        const daySummary = deliveryDays.length === 0 ? '' : `${deliveryDays.length > 1 ? deliveryDays.length : '1'} delivery${deliveryDays.length > 1 ? 's' : ''}`;

        return daySummary ? `${totalSubscriptions}: ${mealSummary}, ${daySummary}` : `${totalSubscriptions}: ${mealSummary}`;
    };

    return (
        <div style={{
            padding: '0',
            width: '100%',
            justifyContent: 'center',
            alignItems: 'center',
        }}>
            {/* Scrollable container for both week headers and date tabs */}
            <div style={{
                overflowX: 'auto',

            }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {/* Top row: Week headers */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}>
                        {Object.entries(datesByWeek).map(([weekKey, datesInWeek], index) => {
                            const { weeklyOrdered, weeklySubscribed } = calculateWeekMealCounts(datesInWeek);
                            // Calculate width based on number of dates in this week
                            const weekWidth = datesInWeek.length * TAB_WIDTH;

                            return (
                                <div
                                    key={weekKey}
                                    style={{
                                        width: `${weekWidth}px`,
                                        padding: '12px 16px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px',
                                        borderRight: datesByWeek.length > 1 ? '2px solid rgb(241, 133, 25)' : 'none',
                                        borderLeft: datesByWeek.length > 1 ? '2px solid rgb(241, 133, 25)' : 'none',
                                    }}
                                >
                                    <div style={{
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        color: '#666666',
                                    }}>
                                        {formatWeekHeader(weekKey)}
                                    </div>
                                    <span style={{
                                        fontSize: '13px',
                                        color: '#999999',
                                        fontWeight: '500',
                                    }}>
                                        {getWeeklySubscriptionsSummary()}
                                    </span>
                                </div>
                            );
                        })}
                    </div>

                    {/* Bottom row: Date tabs */}
                    <div style={{
                        display: 'flex',
                        backgroundColor: '#fff8f0',
                        borderBottom: '1px solid rgb(255, 218, 204)',
                        justifyContent: 'center',
                    }}>
                        {Object.entries(datesByWeek).map(([weekKey, datesInWeek], weekIndex) => (
                            <div
                                key={weekKey}
                                style={{
                                    display: 'flex',
                                    borderRight: weekIndex < Object.keys(datesByWeek).length - 1 ? '2px solid #ffe5cc' : 'none',
                                }}
                            >
                                {datesInWeek.map(deliveryDate => {
                                    const isSelected = selectedDeliveryDate === deliveryDate;
                                    const orderedCount = (ordersByDeliveryDate[deliveryDate] || []).filter(o => o.quantity > 0).length;
                                    const hasOrders = orderedCount > 0;
                                    const isSkipped = deliveryStatusState[deliveryDate]?.isSkipped;
                                    const lockDate = getCutoffTime(deliveryDate);

                                    return (
                                        <button
                                            key={deliveryDate}
                                            onClick={() => onDeliveryDateChange(deliveryDate)}
                                            style={{

                                                flex: '0 0 auto',
                                                width: `${TAB_WIDTH}px`,
                                                padding: '12px 16px',
                                                borderBottom: isSelected ? '4px solid #ffb13c' : '4px solid transparent',
                                                backgroundColor: isSelected ? '#ffffff' : 'transparent',
                                                color: isSelected ? '#333333' : '#666666',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                fontWeight: isSelected ? '600' : '500',
                                                transition: 'all 0.2s ease',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: '8px',
                                                position: 'relative',
                                                outline: 'none',
                                                border: 'none',
                                                borderRight: '1px solid rgb(255, 255, 255)',
                                                borderLeft: '1px solid rgb(255, 255, 255)',
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!isSelected) {
                                                    e.currentTarget.style.backgroundColor = '#ffffff';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!isSelected) {
                                                    e.currentTarget.style.backgroundColor = 'transparent';
                                                }
                                            }}
                                        >
                                            {/* Indicator dot for orders/skipped */}
                                            {(hasOrders || isSkipped) && (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: '8px',
                                                    right: '8px',
                                                    width: '8px',
                                                    height: '8px',
                                                    borderRadius: '50%',
                                                    backgroundColor: isSkipped ? '#d32f2f' : '#4CAF50',
                                                }} />
                                            )}

                                            {/* Date */}
                                            <div style={{
                                                fontSize: '17px',
                                                fontWeight: isSelected ? '600' : '500',
                                                color: isSelected ? '#333333' : '#666666',
                                            }}>
                                                {formatDeliveryDate(deliveryDate)}
                                            </div>

                                            {/* Lock time */}
                                            {lockDate && (
                                                <div style={{
                                                    fontSize: '12px',
                                                    color: isSelected ? '#999999' : '#aaaaaa',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px',
                                                }}>
                                                    <span>⏰</span>
                                                    <span>{formatLockDate(lockDate)}</span>
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Content below tabs - Skip button and meal info */}
            {deliveryDates.map((deliveryDate, dateIndex) => {
                if (selectedDeliveryDate !== deliveryDate) return null;

                // Calculate horizontal position based on tab index

                const leftOffset = (dateIndex + 1) * TAB_WIDTH;

                return (
                    <div key={deliveryDate} style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        marginLeft: `${leftOffset}px`,
                        width: `${TAB_WIDTH}px`,
                        padding: '12px 16px'
                    }}>
                        {/* Skip Delivery Button */}
                        {deliveryStatusState[deliveryDate]?.hasSubscription &&
                            !deliveryStatusState[deliveryDate]?.isSkipped &&
                            !isLocked && (
                                <SkipDelivery
                                    subscriptionSummary={subscriptions.find(sub => sub.nextDeliveryDate === deliveryDate)}
                                    selectedDeliveryDate={deliveryDate}
                                    customerToken={customerToken}
                                    onSkipConfirmed={onSkipConfirmed}
                                    customerName={customerDetails.customer.name}
                                />
                            )}
                    </div>
                );
            })}
        </div>
    );
};

export default DeliveryDateTabs;